import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputImage from "../../../../component/Input/InputImage";
import { getToken } from "../../../../helpers/Jwt";

const EntryUnit = () => {

    const { id } = useParams(); // Get 'id' from endpoint params
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const PATH_UNITS = "api/rentcar/dashboard/unit";

    useEffect(() => {
        if (id) {
            fetchData(id);
        }
    }, [id]);

    const fetchData = async (id) => {
        setLoading(true);
        try {
            await axios.get(`${BASE_URL}${PATH_UNITS}/detail?id=${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                }
            }).then(response => {
                const { nama_unit, merk, tipe, tahun, warna, kapasitas, transmisi, plat_nomor, status } = response.data;

                setInitialValues({
                    nama_unit: nama_unit || '',
                    merk: merk || '',
                    tipe: tipe || '',
                    tahun: tahun || '',
                    warna: warna || '',
                    kapasitas: kapasitas || '',
                    transmisi: transmisi || '',
                    plat_nomor: plat_nomor || '',
                    status: status || 'Tersedia'
                });
            });
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const [initialValues, setInitialValues] = useState({
        id: '',
        nama_unit: '',
        merk: '',
        tipe: '',
        tahun: '',
        warna: '',
        kapasitas: '',
        transmisi: '',
        plat_nomor: '',
        deskripsi: '',
        foto: '',
        status: 'tersedia' // default value
    });

    const validationSchema = Yup.object().shape({
        nama_unit: Yup.string().required('Name Unit is required'),
        merk: Yup.string().required('Merk Unit is required'),
        tipe: Yup.string().required('Tipe Unit is required'),
        tahun: Yup.string().required('Tahun Unit is required'),
        warna: Yup.string().required('Warna Unit is required'),
        status: Yup.string()
            .oneOf(['Tersedia', 'Disewa', 'Servis'], 'Status tidak valid')
            .required('Status unit wajib diisi'),

    });

    const onSubmit = async (values, requests) => {
        try {
            const formData = {
                ...values,
                id: id || undefined, // Include id if it exists                
            };

            if (id) {
                formData['id'] = id;
                await axios.post(`${BASE_URL}${PATH_UNITS}`, formData, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getToken()}`
                    }
                });
            } else {
                await axios.post(`${BASE_URL}${PATH_UNITS}`, formData, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getToken()}`
                    }
                });
            }

            navigate(-1);
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(error.response.data);
        } finally {
            requests.setSubmitting(false);
        }
    };

    if (loading) return <p>Loading...</p>;

    return (
        <div className="mx-auto p-4 bg-white shadow-md rounded">
            <h2 className="text-2xl font-bold mb-4">Form Data Unit</h2>
            <Formik
                enableReintialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}>
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
                    <Form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 py-2">
                            {/* Kolom Kiri */}
                            <div>
                                <label htmlFor="plat_nomor">Plat Nomor</label>
                                <Field
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="plat_nomor"
                                />
                                <ErrorMessage name="plat_nomor" component="p" className="text-red-500 text-xs mt-1" />
                            </div>

                            <div>
                                <label htmlFor="nama_unit">Nama Unit</label>
                                <Field
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="nama_unit"
                                />
                                <ErrorMessage name="nama_unit" component="p" className="text-red-500 text-xs mt-1" />
                            </div>

                            <div>
                                <label htmlFor="merk">Merk</label>
                                <Field
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="merk"
                                />
                                <ErrorMessage name="merk" component="p" className="text-red-500 text-xs mt-1" />
                            </div>

                            <div>
                                <label htmlFor="tipe">Tipe</label>
                                <Field
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="tipe"
                                />
                                <ErrorMessage name="tipe" component="p" className="text-red-500 text-xs mt-1" />
                            </div>

                            <div>
                                <label htmlFor="transmisi">Transmisi</label>
                                <Field
                                    as="select"
                                    name="transmisi"
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    onChange={(e) => setFieldValue("transmisi", e.target.value)}
                                >
                                    <option value="" label="Pilih Transmisi" />
                                    <option value="Automatic" label="Automatic" />
                                    <option value="Manual" label="Manual" />
                                </Field>
                                <ErrorMessage name="transmisi" component="p" className="text-red-500 text-xs mt-1" />
                            </div>

                            <div>
                                <label htmlFor="tahun">Tahun</label>
                                <Field
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="tahun"
                                />
                                <ErrorMessage name="tahun" component="p" className="text-red-500 text-xs mt-1" />
                            </div>

                            <div>
                                <label htmlFor="warna">Warna</label>
                                <Field
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="warna"
                                />
                                <ErrorMessage name="warna" component="p" className="text-red-500 text-xs mt-1" />
                            </div>

                            <div>
                                <label htmlFor="kapasitas">Kapasitas</label>
                                <Field
                                    className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                                    type="text"
                                    name="kapasitas"
                                />
                                <ErrorMessage name="kapasitas" component="p" className="text-red-500 text-xs mt-1" />
                            </div>
                        </div>

                        {/* Input image di luar grid */}
                        <div className="px-6 py-2">
                            <InputImage
                                attr={{
                                    name: "foto",
                                    placeholder: "Foto Unit Kendaraan",
                                    height: 400,
                                    width: 500
                                }}
                                handleCroppedFoto={(value) => {
                                    setFieldValue("foto", value);
                                }}
                            />
                        </div>

                        <div className="px-6 py-2">
                            <label className="block font-medium text-sm text-gray-700 mb-1">
                                Status Unit
                            </label>
                            <div className="flex items-center space-x-6">
                                <label className="flex items-center space-x-2">
                                    <Field
                                        type="radio"
                                        name="status"
                                        value="Tersedia"
                                        className="form-radio text-green-600"
                                    />
                                    <span>Tersedia</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <Field
                                        type="radio"
                                        name="status"
                                        value="Disewa"
                                        className="form-radio text-yellow-600"
                                    />
                                    <span>Disewa</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <Field
                                        type="radio"
                                        name="status"
                                        value="Servis"
                                        className="form-radio text-red-600"
                                    />
                                    <span>Servis</span>
                                </label>
                            </div>
                        </div>


                        {/* Tombol Submit */}
                        <div className="px-6 py-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring focus:ring-blue-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EntryUnit;
